import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ComplianceRequestGridItem } from 'src/app/compliance/compliance-requests/compliance-request-grid-item';
import {
    bulkStatusUpdate,
    bulkStatusUpdateFailure,
    bulkStatusUpdateSuccess,
    getComplianceRequestsAssignees,
    getComplianceRequestsAssigneesFailure,
    getComplianceRequestsAssigneesFilter,
    getComplianceRequestsAssigneesFilterFailure,
    getComplianceRequestsAssigneesFilterSuccess,
    getComplianceRequestsAssigneesSuccess,
    getListOfComplianceRequestsFailure,
    getListOfComplianceRequestsSuccess,
    getListOfComplianceTicketRequestsFailure,
    getListOfComplianceTicketRequestsSuccess,
    getListOfLabTicketRequestsFailure,
    getListOfLabTicketRequestsSuccess,
    setComplianceRequestsFilter,
    setComplianceTicketRequestsFilter,
    setLabTicketRequestsFilter
} from 'src/app/compliance/store/actions/compliance-requests.actions';
import {
    selectComplianceTicketRequestsFilter,
    selectFilter,
    selectLabTicketRequestsFilter,
    selectStatusUpdate
} from 'src/app/compliance/store/selectors/compliance-requests.selectors';
import { ComplianceState } from 'src/app/compliance/store/state/compliance.state';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { FetchArgs, PagingTokenFixed } from 'src/app/shared/models/core';
import { Lookup } from 'src/app/shared/models/lookup.model';
import { ComplianceRequestsClient } from '../../api/compliance-requests-client.service';
import { gridDate } from '../../api/migration/grid-date.function';
import { ComplianceRequestFilterDto } from '../../api/models/compliance-request-filter-dto.model';
import { PagingTokenOfComplianceRequestGridItemDto } from '../../api/models/paging-token-of-compliance-request-grid-item-dto.model';

@Injectable()
export class ComplianceRequestsEffects {
    getListOfComplianceRequests$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(setComplianceRequestsFilter),
            concatLatestFrom(() => this._store.select(selectFilter)),
            switchMap(([, filters]) => {
                return this.searchComplianceRequests(filters).pipe(
                    map((res: PagingTokenFixed<ComplianceRequestGridItem[]>) =>
                        getListOfComplianceRequestsSuccess({ complianceRequests: res })
                    ),
                    catchError((result) => of(getListOfComplianceRequestsFailure({ error: result })))
                );
            })
        );
    });

    getListOfLabTicketRequests$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(setLabTicketRequestsFilter),
            concatLatestFrom(() => this._store.select(selectLabTicketRequestsFilter)),
            switchMap(([, filters]) => {
                return this.searchLabTicketRequests(filters).pipe(
                    map((res: PagingTokenFixed<ComplianceRequestGridItem[]>) =>
                        getListOfLabTicketRequestsSuccess({ complianceRequests: res })
                    ),
                    catchError((result) => of(getListOfLabTicketRequestsFailure({ error: result })))
                );
            })
        );
    });

    getListOfComplianceTicketRequests$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(setComplianceTicketRequestsFilter),
            concatLatestFrom(() => this._store.select(selectComplianceTicketRequestsFilter)),
            switchMap(([, filters]) => {
                return this.searchComplianceTicketRequests(filters).pipe(
                    map((res: PagingTokenFixed<ComplianceRequestGridItem[]>) =>
                        getListOfComplianceTicketRequestsSuccess({ complianceRequests: res })
                    ),
                    catchError((result) => of(getListOfComplianceTicketRequestsFailure({ error: result })))
                );
            })
        );
    });

    getAssignees$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getComplianceRequestsAssignees),
            switchMap(() => {
                return this.complianceRequestsClient.getComplianceRequestAssignees(null).pipe(
                    map((data: Lookup[]) => getComplianceRequestsAssigneesSuccess({ assignees: data })),
                    catchError((error: any) => of(getComplianceRequestsAssigneesFailure({ error: error })))
                );
            })
        );
    });

    getAssigneesFilter$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getComplianceRequestsAssigneesFilter),
            switchMap(() => {
                return this.complianceRequestsClient.getComplianceRequestAssigneesFilter().pipe(
                    map(data => getComplianceRequestsAssigneesFilterSuccess({ assignees: data })),
                    catchError((error: unknown) => of(getComplianceRequestsAssigneesFilterFailure({ error: error })))
                );
            })
        );
    });

    showGetListOfComplianceRequestsFailureMessage$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getListOfComplianceRequestsFailure),
            map(() => showToasterMessage({ message: 'Failed to load Compliance Requests.' }))
        );
    });

    showGetListOfLabTicketRequestsFailureMessage$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getListOfLabTicketRequestsFailure),
            map(() => showToasterMessage({ message: 'Failed to load Compliance Requests.' }))
        );
    });

    showGetListOfComplianceTicketRequestsFailureMessage$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getListOfComplianceTicketRequestsFailure),
            map(() => showToasterMessage({ message: 'Failed to load Compliance Requests.' }))
        );
    });

    bulkStatusUpdate$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(bulkStatusUpdate),
            concatLatestFrom(() => this._store.select(selectStatusUpdate)),
            switchMap(([, statusUpdate]) => {
                return this.complianceRequestsClient.bulkStatusUpdate(statusUpdate).pipe(
                    map(() =>
                        this._store.dispatch(
                            showToasterMessage({
                                message: `${statusUpdate.requestIds.length} compliance requests have been updated`,
                                duration: 10000
                            })
                        )
                    ),
                    map(() => bulkStatusUpdateSuccess({ updatedRequestIds: statusUpdate.requestIds })),
                    catchError(() => {
                        this._store.dispatch(
                            showToasterMessage({ message: `There was a problem with updating the ticket status.` })
                        );
                        return of(bulkStatusUpdateFailure());
                    })
                );
            })
        );
    });

    private searchComplianceTicketRequests(args: FetchArgs): Observable<PagingTokenOfComplianceRequestGridItemDto > {
        const filter: ComplianceRequestFilterDto = {
            ...args.filter,
            ticketTypes: args.filter?.ticketType, // name mismatch
            statuses: args.filter?.status, // name mismatch
            ticketProgressStatuses: args.filter?.progressStatus, // name mistmatch
            ticketPriorities: args.filter?.priority,  // name mistmatch
            contractStatuses: args.filter?.contractStatus, // name mistmatch
        };

        return this.complianceRequestsClient.searchComplianceTicketRequests(
            filter.id, // filter_Id
            filter.ticketTypes, // filter_TicketTypes
            filter.statuses, // filter_Statuses
            filter.ticketProgressStatuses, // filter_TicketProgressStatuses
            filter.candidate, // filter_Candidate
            filter.assignedToIds, // filter_AssignedToIds
            filter.contractFacilityName, // filter_ContractFacilityName
            filter.createdBy, // filter_CreatedBy
            gridDate(filter.docDueDateFrom), // filter_DocDueDateFrom
            gridDate(filter.docDueDateTo), // filter_DocDueDateTo
            gridDate(filter.createdOnFrom), // filter_CreatedOnFrom
            gridDate(filter.createdOnTo), // filter_CreatedOnTo
            filter.ticketPriorities, // filter_TicketPriorities
            filter.contractStatuses, // filter_ContractStatuses
            gridDate(filter.contractStartDateFrom), // filter_ContractStartDateFrom
            gridDate(filter.contractStartDateTo), // filter_ContractStartDateTo
            args.pageSize, // pageSize
            args.skip, // skip
            args.sort?.field, // sort_Field
            args.sort?.dir as any, // sort_Type
            undefined // paginated
        );
    }

    private searchLabTicketRequests(args: FetchArgs): Observable<PagingTokenOfComplianceRequestGridItemDto> {
        const filter: ComplianceRequestFilterDto = {
            ...args.filter,
            ticketTypes: args.filter?.ticketType, // name mismatch
            statuses: args.filter?.status, // name mismatch
            ticketProgressStatuses: args.filter?.progressStatus, // name mistmatch
            ticketPriorities: args.filter?.priority,  // name mistmatch
            contractStatuses: args.filter?.contractStatus, // name mistmatch
        };

        return this.complianceRequestsClient.searchLabTicketRequests(
            filter.id, // filter_Id
            filter.ticketTypes, // filter_TicketTypes
            filter.statuses, // filter_Statuses
            filter.ticketProgressStatuses, // filter_TicketProgressStatuses
            filter.candidate, // filter_Candidate
            filter.assignedToIds, // filter_AssignedToIds
            filter.contractFacilityName, // filter_ContractFacilityName
            filter.createdBy, // filter_CreatedBy
            gridDate(filter.docDueDateFrom), // filter_DocDueDateFrom
            gridDate(filter.docDueDateTo), // filter_DocDueDateTo
            gridDate(filter.createdOnFrom), // filter_CreatedOnFrom
            gridDate(filter.createdOnTo), // filter_CreatedOnTo
            filter.ticketPriorities, // filter_TicketPriorities
            filter.contractStatuses, // filter_ContractStatuses
            gridDate(filter.contractStartDateFrom), // filter_ContractStartDateFrom
            gridDate(filter.contractStartDateTo), // filter_ContractStartDateTo
            args.pageSize, // pageSize
            args.skip, // skip
            args.sort?.field, // sort_Field
            args.sort?.dir as any, // sort_Type
            undefined // paginated
        );
    }

    private searchComplianceRequests(args: FetchArgs): Observable<PagingTokenOfComplianceRequestGridItemDto> {
        const filter: ComplianceRequestFilterDto = {
            ...args.filter,
            ticketTypes: args.filter?.ticketType, // name mismatch
            statuses: args.filter?.status, // name mismatch
            ticketProgressStatuses: args.filter?.progressStatus, // name mistmatch
            ticketPriorities: args.filter?.priority,  // name mistmatch
            contractStatuses: args.filter?.contractStatus, // name mistmatch
        };

        return this.complianceRequestsClient.search(
            filter.id, // filter_Id
            filter.ticketTypes, // filter_TicketTypes
            filter.statuses, // filter_Statuses
            filter.ticketProgressStatuses, // filter_TicketProgressStatuses
            filter.candidate, // filter_Candidate           
            filter.assignedToIds, // filter_AssignedToIds
            filter.contractFacilityName, // filter_ContractFacilityName
            filter.createdBy, // filter_CreatedBy
            gridDate(filter.docDueDateFrom), // filter_DocDueDateFrom
            gridDate(filter.docDueDateTo), // filter_DocDueDateTo
            gridDate(filter.createdOnFrom), // filter_CreatedOnFrom
            gridDate(filter.createdOnTo), // filter_CreatedOnTo
            filter.ticketPriorities, // filter_TicketPriorities
            filter.contractStatuses, // filter_ContractStatuses
            gridDate(filter.contractStartDateFrom), // filter_ContractStartDateFrom
            gridDate(filter.contractStartDateTo), // filter_ContractStartDateTo
            args.pageSize, // pageSize
            args.skip, // skip
            args.sort?.field, // sort_Field
            args.sort?.dir as any, // sort_Type
            undefined // paginated
        );
    }


    constructor(
        private readonly _actions$: Actions,
        private readonly _store: Store<ComplianceState>,
        private readonly complianceRequestsClient: ComplianceRequestsClient) {}
}
